import React, { useEffect, useRef, useState } from "react";
import { FaFilePdf, FaUpload, FaDownload, FaTrash, FaSync } from "react-icons/fa";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../firebase";
import { useToast } from "./Toast";
import { useAppDialog } from "./AppDialog";
import { logAudit } from "../utils/auditLog";
import { getAuthHeaders, HABI_API_BASE_URL } from "../utils/habiApi";
import { colors, radius, spacing, typography, cardBase, btnPrimary, btnSecondary, btnDanger } from "../utils/designTokens";

/**
 * Reglamentos del edificio (copropiedad + interno).
 *
 * Viven como campos del doc `building/{id}` (reglamento*Url/FileName/UploadedAt)
 * en Storage `buildings/{id}/reglamentos/{tipo}.pdf`. Los leen la pantalla del
 * residente (DocumentacionEdificio) y Habi, que los indexa en su RAG para
 * responder citando el PDF (/api/index-building-docs). Antes estaban en
 * Editar Edificio → Documentos; ahora se gestionan solo desde Documentos.
 * Los ven siempre propietarios e inquilinos.
 */
const REGLAMENTOS = [
  {
    tipo: "copropiedad",
    label: "Reglamento de Copropiedad",
    descripcion: "Normas legales de la propiedad horizontal del edificio.",
    urlField: "reglamentoCopropiedadUrl",
    nameField: "reglamentoCopropiedadFileName",
    dateField: "reglamentoCopropiedadUploadedAt",
  },
  {
    tipo: "interno",
    label: "Reglamento Interno",
    descripcion: "Reglas de convivencia y uso de áreas comunes.",
    urlField: "reglamentoInternoUrl",
    nameField: "reglamentoInternoFileName",
    dateField: "reglamentoInternoUploadedAt",
  },
];

const fmtDate = (v) => {
  if (!v) return null;
  const d = v.toDate ? v.toDate() : v.seconds ? new Date(v.seconds * 1000) : new Date(v);
  return isNaN(d) ? null : d.toLocaleDateString("es-UY");
};

export default function ReglamentosEdificio({ buildingDocId }) {
  const { showToast } = useToast();
  const { confirmDialog } = useAppDialog();
  const [data, setData] = useState({});
  const [uploading, setUploading] = useState(null);
  const [reindexing, setReindexing] = useState(false);
  const inputRefs = { copropiedad: useRef(null), interno: useRef(null) };

  // Se lee fresco del doc (el building del contexto puede estar desactualizado).
  useEffect(() => {
    if (!buildingDocId) return;
    let alive = true;
    getDoc(doc(db, "building", buildingDocId))
      .then((s) => { if (alive) setData(s.exists() ? s.data() : {}); })
      .catch((e) => console.error("[Reglamentos] no se pudo leer el edificio:", e));
    return () => { alive = false; };
  }, [buildingDocId]);

  // Pide a Habi reindexar los reglamentos (OCR incluido). Devuelve la respuesta.
  const reindex = async () => {
    const headers = await getAuthHeaders();
    const resp = await fetch(`${HABI_API_BASE_URL}/api/index-building-docs`, {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ building_id: String(buildingDocId) }),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.detail || `Error ${resp.status} al reindexar.`);
    }
    return resp.json();
  };

  const reindexEnBackground = () => {
    reindex().catch((e) => console.warn("[Reglamentos] reindex Habi falló:", e));
  };

  const handleUpload = async (r, file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { showToast("Solo se permiten archivos PDF", "error"); return; }
    if (file.size > 10 * 1024 * 1024) { showToast("El archivo no puede superar los 10MB", "error"); return; }
    setUploading(r.tipo);
    try {
      const fileRef = storageRef(storage, `buildings/${buildingDocId}/reglamentos/${r.tipo}.pdf`);
      await uploadBytes(fileRef, file, { contentType: "application/pdf" });
      const url = await getDownloadURL(fileRef);
      const updates = { [r.urlField]: url, [r.nameField]: file.name, [r.dateField]: serverTimestamp() };
      await updateDoc(doc(db, "building", buildingDocId), updates);
      setData((prev) => ({ ...prev, ...updates, [r.dateField]: new Date() }));
      logAudit("upload_reglamento", { entityType: "building", entityId: buildingDocId, meta: { tipo: r.tipo, fileName: file.name } });
      showToast("Reglamento subido. Habi lo está procesando.", "success");
      reindexEnBackground();
    } catch (e) {
      console.error("Error subiendo reglamento:", e);
      showToast("Error al subir el archivo: " + e.message, "error");
    } finally {
      setUploading(null);
    }
  };

  const handleDelete = async (r) => {
    const ok = await confirmDialog(
      "¿Seguro que querés eliminar este reglamento? Habi dejará de poder responder sobre él.",
      { title: "Eliminar reglamento", danger: true, confirmLabel: "Eliminar" }
    );
    if (!ok) return;
    setUploading(r.tipo);
    try {
      try {
        await deleteObject(storageRef(storage, `buildings/${buildingDocId}/reglamentos/${r.tipo}.pdf`));
      } catch (e) {
        console.warn("Storage delete falló (puede no existir):", e);
      }
      const updates = { [r.urlField]: null, [r.nameField]: null, [r.dateField]: null };
      await updateDoc(doc(db, "building", buildingDocId), updates);
      setData((prev) => ({ ...prev, ...updates }));
      logAudit("delete_reglamento", { entityType: "building", entityId: buildingDocId, meta: { tipo: r.tipo } });
      showToast("Reglamento eliminado", "success");
      reindexEnBackground();
    } catch (e) {
      console.error("Error eliminando reglamento:", e);
      showToast("Error al eliminar: " + e.message, "error");
    } finally {
      setUploading(null);
    }
  };

  const handleManualReindex = async () => {
    setReindexing(true);
    try {
      const res = await reindex();
      const total = Object.values(res.results || {}).reduce((n, x) => n + (x?.chunks_indexed || 0), 0);
      const fallo = Object.values(res.results || {}).some((x) => x && !x.success);
      if (fallo) showToast("Habi reindexó con errores. Probá de nuevo en unos minutos.", "error");
      else if (total > 0) showToast(`Habi reindexó ${total} fragmento(s). Ya puede responder sobre los reglamentos.`, "success");
      else showToast("No hay reglamentos cargados para indexar.", "info");
    } catch (e) {
      showToast(e.message || "Error al reindexar.", "error");
    } finally {
      setReindexing(false);
    }
  };

  return (
    <section style={styles.card} aria-labelledby="reglamentos-title">
      <div style={styles.header}>
        <div>
          <h3 id="reglamentos-title" style={styles.title}>Reglamentos</h3>
          <p style={styles.subtitle}>
            Visibles para propietarios e inquilinos. Habi los lee para responder consultas citando el PDF
            (acepta PDFs escaneados: aplica OCR, puede demorar unos minutos).
          </p>
        </div>
        <button type="button" onClick={handleManualReindex} disabled={reindexing}
          style={{ ...btnSecondary, ...styles.smallBtn, opacity: reindexing ? 0.6 : 1 }}
          title="Si Habi no encuentra el reglamento aunque esté subido, reindexar lo vuelve a procesar">
          <FaSync size={12} /> {reindexing ? "Reindexando…" : "Reindexar en Habi"}
        </button>
      </div>

      <div style={styles.grid}>
        {REGLAMENTOS.map((r) => {
          const url = data[r.urlField];
          const fecha = fmtDate(data[r.dateField]);
          const busy = uploading === r.tipo;
          return (
            <div key={r.tipo} style={styles.item}>
              <div style={{ ...styles.icon, backgroundColor: url ? colors.successLight : colors.gray100 }}>
                <FaFilePdf size={18} color={url ? colors.successDark : colors.gray600} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.itemTitle}>{r.label}</div>
                <div style={styles.itemDesc}>{r.descripcion}</div>
                <div style={styles.itemFile}>
                  {url ? `${data[r.nameField] || "documento.pdf"}${fecha ? ` · subido el ${fecha}` : ""}` : "Sin documento cargado"}
                </div>
                <div style={styles.actions}>
                  <input ref={inputRefs[r.tipo]} type="file" accept="application/pdf" style={{ display: "none" }}
                    aria-label={`Subir ${r.label}`}
                    onChange={(e) => { handleUpload(r, e.target.files?.[0]); e.target.value = ""; }} />
                  {url && (
                    <a href={url} target="_blank" rel="noopener noreferrer" style={{ ...btnSecondary, ...styles.smallBtn, textDecoration: "none" }}>
                      <FaDownload size={12} /> Ver
                    </a>
                  )}
                  <button type="button" disabled={busy} onClick={() => inputRefs[r.tipo].current?.click()}
                    style={{ ...btnPrimary, ...styles.smallBtn, opacity: busy ? 0.6 : 1 }}>
                    <FaUpload size={12} /> {busy ? "Subiendo…" : url ? "Reemplazar" : "Subir PDF"}
                  </button>
                  {url && (
                    <button type="button" disabled={busy} onClick={() => handleDelete(r)}
                      style={{ ...btnDanger, ...styles.smallBtn, opacity: busy ? 0.6 : 1 }}>
                      <FaTrash size={12} /> Eliminar
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

const styles = {
  card: { ...cardBase, padding: spacing[5], marginBottom: spacing[4] },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: spacing[3], flexWrap: "wrap", marginBottom: spacing[4] },
  title: { margin: 0, fontSize: typography.sizes.lg, fontWeight: 400, color: colors.gray900 },
  subtitle: { margin: `${spacing[1]}px 0 0`, fontSize: typography.sizes.sm, color: colors.gray600, maxWidth: 620 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: spacing[3] },
  item: { display: "flex", gap: spacing[3], border: `1px solid ${colors.gray200}`, borderRadius: radius.md, padding: spacing[4], background: colors.white },
  icon: { width: 40, height: 40, borderRadius: radius.sm, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  itemTitle: { fontSize: typography.sizes.md, color: colors.gray900 },
  itemDesc: { fontSize: typography.sizes.sm, color: colors.gray600, margin: "2px 0 6px" },
  itemFile: { fontSize: typography.sizes.sm, color: colors.gray700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  actions: { display: "flex", gap: spacing[2], marginTop: spacing[3], flexWrap: "wrap" },
  smallBtn: { padding: "6px 12px", fontSize: typography.sizes.base, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" },
};
